import PageEditSessionRecord from "models/view-models/page-edit-session-record";
import usePageEditSessions from "pages/notebooks/use-page-edit-sessions";
import React, {
    PropsWithChildren,
    createContext,
    useContext,
    useEffect,
    useRef,
} from "react";
import useCurrentIdentity from "utilities/hooks/use-current-identity";

// -------------------------------------------------------------------------------------------------
// #region Types
// -------------------------------------------------------------------------------------------------

interface PageEditSessionContextInterface {
    activeEditSessions: PageEditSessionRecord[];
    startEditSession: (blockId: number) => Promise<boolean>;
    updateEditSession: (
        sessionId: number,
        blockId?: number
    ) => Promise<boolean>;
    endEditSession: (sessionId: number) => Promise<boolean>;
    pageId?: number;
    isInEditMode: boolean;
}

// #endregion Types

// -------------------------------------------------------------------------------------------------
// #region Context
// -------------------------------------------------------------------------------------------------

const defaultUpdater = async (blockId: number) => false;

export const defaultState = {
    activeEditSessions: [],
    startEditSession: defaultUpdater,
    updateEditSession: defaultUpdater,
    endEditSession: defaultUpdater,
    pageId: undefined,
    isInEditMode: false,
};

export const PageEditSessionContext =
    createContext<PageEditSessionContextInterface>(defaultState);

// #endregion Context

// -------------------------------------------------------------------------------------------------
// #region Provider
// -------------------------------------------------------------------------------------------------
interface PageEditSessionContextProviderProps extends PropsWithChildren {
    pageId?: number;
    isInEditMode: boolean;
}
const PageEditSessionContextProvider = (
    props: PageEditSessionContextProviderProps
) => {
    // Sessions for each block
    const { pageId, isInEditMode } = props;

    const {
        activeEditSessions,
        startEditSession,
        updateEditSession,
        endEditSession,
    } = usePageEditSessions(isInEditMode, pageId);

    return (
        <PageEditSessionContext.Provider
            value={{
                activeEditSessions,
                startEditSession,
                updateEditSession,
                endEditSession,
                pageId,
                isInEditMode,
            }}>
            {props.children}
        </PageEditSessionContext.Provider>
    );
};

// #endregion Provider

// -------------------------------------------------------------------------------------------------
// #region Hook
// -------------------------------------------------------------------------------------------------
interface UsePageEditSessionContextOptions {
    handleBlocksChangedByAnotherUser?: (changedBlockIds: number[]) => void;
}

export const usePageEditSessionContext = ({
    handleBlocksChangedByAnotherUser,
}: UsePageEditSessionContextOptions = {}) => {
    const {
        activeEditSessions,
        endEditSession,
        startEditSession,
        updateEditSession,
        pageId,
        isInEditMode,
    } = useContext(PageEditSessionContext);
    const { currentIdentity } = useCurrentIdentity();
    const activePageEditSession = pageId
        ? activeEditSessions.find((a) => a.pageId === pageId && !a.blockId)
        : undefined;
    const getSessionForBlock = (blockId?: number) => {
        if (!blockId) return undefined;
        return activeEditSessions.find(
            (a) => !!a.blockId && a.blockId === blockId
        );
    };

    const currentUserActiveSession = activeEditSessions.find(
        (a) => a.userId === currentIdentity?.userId()
    );

    const currentUserActiveSessionId = currentUserActiveSession?.id;

    useEffect(() => {
        if (!isInEditMode && !!currentUserActiveSessionId) {
            endEditSession(currentUserActiveSessionId);
        }
    }, [currentUserActiveSessionId, endEditSession, isInEditMode]);

    const previousActiveSessions = useRef(activeEditSessions);

    // If prev sessions and current sessions are different, a block has been changed by another user, and we need to refetch the blocks
    useEffect(() => {
        if (
            previousActiveSessions.current &&
            activeEditSessions &&
            previousActiveSessions.current !== activeEditSessions &&
            handleBlocksChangedByAnotherUser
        ) {
            const blocksChangedByAnotherUser = activeEditSessions
                .filter(
                    (session) =>
                        session.userId !== currentIdentity?.userId() &&
                        !!session.blockId
                )
                .map((session) => session.blockId);
            handleBlocksChangedByAnotherUser(
                blocksChangedByAnotherUser as number[]
            );
            previousActiveSessions.current = activeEditSessions;
        }
    }, [activeEditSessions, currentIdentity, handleBlocksChangedByAnotherUser]);

    return {
        activeEditSessions,
        activePageEditSession,
        endEditSession,
        startEditSession,
        updateEditSession,
        getSessionForBlock,
        currentUserActiveSession,
    };
};

// #endregion Hook

// -------------------------------------------------------------------------------------------------
// #region Exports
// -------------------------------------------------------------------------------------------------

export default PageEditSessionContextProvider;

// #endregion Exports
